// Port of SBotToolResultEnvelope + SBotToolHandle + SBotToolJSON from
// Models/SBotChatModels.swift. Tool results are JSON strings with sorted keys
// and ISO-8601 dates so iOS and web transcripts stay byte-compatible.

export type ToolJSONValue = string | number | boolean | null | ToolJSONValue[] | { [key: string]: ToolJSONValue } | Date;

export interface SBotToolApprovalCard {
  approvalID: string;
  title: string;
  summary: string;
  status: string;
  actionType: string;
  leagueID: string;
  targetHandle?: string | undefined;
  expiresAt: Date;
}

// MARK: - SBotToolJSON (sorted keys, ISO dates, compact)

export function toolJSONString(value: Record<string, ToolJSONValue>): string {
  try {
    return JSON.stringify(sortValue(value));
  } catch {
    return '{"error":"Could not serialize tool result.","ok":false}';
  }
}

function sortValue(value: ToolJSONValue): unknown {
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map(sortValue);
  if (typeof value === "object" && value !== null) {
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(value).sort()) {
      sorted[key] = sortValue((value as Record<string, ToolJSONValue>)[key] as ToolJSONValue);
    }
    return sorted;
  }
  return value;
}

// MARK: - Envelope

export function toolSuccessEnvelope(tool: string, payload: Record<string, ToolJSONValue>, approval?: SBotToolApprovalCard): string {
  const envelope: Record<string, ToolJSONValue> = { ok: true, tool, data: payload };
  if (approval) {
    envelope.approval = {
      approvalID: approval.approvalID,
      title: approval.title,
      summary: approval.summary,
      status: approval.status,
      actionType: approval.actionType,
      leagueID: approval.leagueID,
      targetHandle: approval.targetHandle ?? null,
      expiresAt: approval.expiresAt,
    };
  }
  return toolJSONString(envelope);
}

export function toolFailureEnvelope(
  tool: string,
  error: unknown,
  context?: { approvalId: string; actionType?: string },
): string {
  const message = error instanceof Error ? error.message : String(error);
  return toolJSONString({ ok: false, tool, error: message, ...context });
}

export function isErrorPayload(content: string): boolean {
  const parsed = parseObject(content);
  if (!parsed) return false;
  const ok = parsed.ok;
  if (typeof ok === "boolean") return !ok;
  if (typeof ok === "string") return ok.toLowerCase() === "false";
  return false;
}

export function approvalCardFromContent(content: string): SBotToolApprovalCard | undefined {
  const parsed = parseObject(content);
  const approval = parsed?.approval;
  if (typeof approval !== "object" || approval === null || Array.isArray(approval)) return undefined;
  const record = approval as Record<string, unknown>;
  const approvalID = asString(record.approvalID);
  const title = asString(record.title);
  const summary = asString(record.summary);
  const status = asString(record.status);
  const actionType = asString(record.actionType);
  const leagueID = asString(record.leagueID);
  const expiresAtRaw = asString(record.expiresAt);
  if (!approvalID || !title || !summary || !status || !actionType || !leagueID || !expiresAtRaw) return undefined;
  const expiresAt = new Date(expiresAtRaw);
  if (Number.isNaN(expiresAt.getTime())) return undefined;
  return {
    approvalID,
    title,
    summary,
    status,
    actionType,
    leagueID,
    targetHandle: asString(record.targetHandle),
    expiresAt,
  };
}

function parseObject(content: string): Record<string, unknown> | undefined {
  const trimmed = content.trim();
  if (!trimmed.startsWith("{")) return undefined;
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : undefined;
  } catch {
    return undefined;
  }
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

// MARK: - Handles (SBotToolHandle)

export const HANDLE_PREFIXES = {
  league: "league",
  template: "template",
  competition: "competition",
  organization: "organization",
  invite: "invite",
} as const;

export function makeHandle(prefix: string, id: string): string {
  return `${prefix}:${id}`;
}

export class SBotToolHandleError extends Error {}

/** Mirrors SBotToolHandle.parse: splits on the first ":", validates prefix + non-empty id. */
export function parseHandle(handle: string, expectedPrefix: string): string {
  const separatorIndex = handle.indexOf(":");
  const prefix = separatorIndex >= 0 ? handle.slice(0, separatorIndex) : "";
  const id = separatorIndex >= 0 ? handle.slice(separatorIndex + 1) : "";
  if (prefix !== expectedPrefix || id.trim().length === 0) {
    throw new SBotToolHandleError(`Expected a ${expectedPrefix}:<id> handle, got '${handle}'.`);
  }
  return id;
}
